import { Record, Map, List, Set, Iterable, fromJS } from 'immutable'
import { getPrototype } from '@aytm/lib/common/func'

const VALUE_MAPPERS = 'function' == typeof Symbol ? Symbol('valueMappers') : '@@ImmutableModel@valueMappers'
const SUPER = 'function' == typeof Symbol ? Symbol('super') : '@@ImmutableModel@super'

export default function ImmutableModel(valueMappers = {}, ExtendsModel, { RecordName, reactive } = {}) {
  const extendedValueMappers =
    'function' == typeof ExtendsModel && getPrototype(ExtendsModel)[VALUE_MAPPERS]
      ? Object.assign({}, getPrototype(ExtendsModel)[VALUE_MAPPERS], valueMappers)
      : valueMappers
  class ImmutableModel extends Record(
    Object.keys(extendedValueMappers).reduce(
      (defaultValues, key) => ((defaultValues[key] = extendedValueMappers[key](void 0, key, Map())), defaultValues),
      {}
    ),
    RecordName
  ) {
    constructor(values = {}) {
      const valuesMap = Map(values)
      super(
        Map().withMutations(mappedValues =>
          Object.keys(extendedValueMappers).reduce(
            (mappedValues, key) => mappedValues.set(key, extendedValueMappers[key](valuesMap.get(key), key, valuesMap)),
            mappedValues
          )
        )
      )
    }
    set(k, v) {
      return null != reactive && 'object' == typeof reactive && Array.isArray(reactive[k])
        ? this.withMutations(model =>
            reactive[k].reduce(
              (model, reactiveKey) =>
                super.set.call(
                  model,
                  reactiveKey,
                  'function' == typeof extendedValueMappers[reactiveKey]
                    ? extendedValueMappers[reactiveKey](model.get(reactiveKey), reactiveKey, model)
                    : model.get(reactiveKey)
                ),
              super.set.call(
                model,
                k,
                'function' == typeof extendedValueMappers[k] ? extendedValueMappers[k](v, k, model) : v
              )
            )
          )
        : super.set(k, 'function' == typeof extendedValueMappers[k] ? extendedValueMappers[k](v, k, this) : v)
    }
  }
  ImmutableModel.prototype.isImmutableModel = true
  ImmutableModel[VALUE_MAPPERS] = extendedValueMappers
  ImmutableModel.valueMappers = function () {
    return extendedValueMappers
  }
  return ImmutableModel
}

ImmutableModel.isImmutableModel = function (maybeImmutableModel) {
  return Boolean(
    null != maybeImmutableModel && 'object' == typeof maybeImmutableModel && maybeImmutableModel.isImmutableModel
  )
}

const functionOwnPropertyNames = Object.getOwnPropertyNames(function () {}).reduce(
  (names, name) => ((names[name] = true), names),
  {}
)

ImmutableModel.decorator = function ({ name, extends: ExtendsModel } = {}) {
  return function (ModelClass) {
    ModelClass.of ||
      (ModelClass.of = function (values = {}) {
        return values instanceof ModelClass ? values : new ModelClass(values)
      })
    ModelClass.empty ||
      (ModelClass.empty = function () {
        return ModelClass.of()
      })
    if (('string' == typeof name && name.length) || ('string' == typeof ModelClass.name && ModelClass.name.length)) {
      ModelClass.prototype[`is${name || ModelClass.name}`] = true
      ModelClass[`is${name || ModelClass.name}`] = function (maybeModel) {
        return Boolean(
          null != maybeModel && 'object' == typeof maybeModel && maybeModel[`is${name || ModelClass.name}`]
        )
      }
    }
    const super_clone =
      ('function' == typeof ModelClass.prototype.clone
        ? 'function' == typeof ModelClass.prototype.clone[SUPER]
          ? ModelClass.prototype.clone[SUPER]
          : ModelClass.prototype.clone
        : null) ||
      ('function' == typeof ExtendsModel &&
      'function' == typeof ExtendsModel.prototype.clone &&
      'function' == typeof ExtendsModel.prototype.clone[SUPER]
        ? ExtendsModel.prototype.clone[SUPER]
        : null)

    ModelClass.prototype.clone = function (...cloneArgs) {
      const nestedClone = this.withMutations(nestedClone => {
        nestedClone.entrySeq().forEach(([k, v]) => {
          nestedClone.set(
            k,
            null != v && 'object' == typeof v && 'function' == typeof v.clone ? v.clone(...cloneArgs) : v
          )
        })
        return nestedClone
      })
      return null == super_clone ? nestedClone : super_clone.apply(nestedClone, cloneArgs)
    }
    ModelClass.prototype.clone[SUPER] = super_clone
    // copy instance properties from ExtendsModel
    if ('function' == typeof ExtendsModel) {
      Object.getOwnPropertyNames(ExtendsModel).forEach(function (propName) {
        if (!functionOwnPropertyNames[propName] && void 0 === ModelClass[propName]) {
          ModelClass[propName] = ExtendsModel[propName]
        }
      })
      Object.getOwnPropertyNames(ExtendsModel.prototype).forEach(function (propName) {
        if (!functionOwnPropertyNames[propName] && void 0 === ModelClass.prototype[propName]) {
          ModelClass.prototype[propName] = ExtendsModel.prototype[propName]
        }
      })
      // if (ExtendsModel.prototype.constructor && ModelClass.prototype.constructor) {
      //   Object.getOwnPropertyNames(ExtendsModel.prototype.constructor).forEach(function(propName) {
      //     if (void 0 === ModelClass.prototype.constructor[propName]) {
      //       ModelClass.prototype.constructor[propName] = ExtendsModel.prototype.constructor[propName]
      //     }
      //   })
      // }
    }
  }
}

export function asValue(x) {
  return x
}

export function asConst(constValue) {
  return function () {
    return constValue
  }
}

export function asBoolean(x) {
  return Boolean(x)
}

export function asString(x) {
  return null == x ? '' : 'string' == typeof x ? x : x.toString()
}

export function asInteger(x) {
  return parseInt(x, 10)
}

export function asFloat(x) {
  return parseFloat(x)
}

export function asArray(x) {
  return Array.isArray(x) ? x : Iterable.isIterable(x) && 'function' == typeof x.toArray ? x.toArray() : []
}

export function asNullOr(asSomething) {
  return function (x) {
    return null == x ? null : asSomething(x)
  }
}

// AYTM-12193 / AYTM-12710 OQ-ED-28: `asNullOr(asInteger)` was lossy for empty / whitespace /
// non-numeric input — `null == ''` is false, so an empty CSV cell reached `parseInt('', 10)` =
// `NaN`, and the editor card rendered the literal string "NaN". This guard returns `null` for
// every non-integer shape (null, undefined, '', '   ', 'abc'), so blank cells render blank.
export function asNullOrInteger(x) {
  if (null == x) return null
  const trimmed = 'string' === typeof x ? x.trim() : x
  if ('' === trimmed) return null
  const n = parseInt(trimmed, 10)
  return Number.isNaN(n) ? null : n
}

export function asOneOf(variants = Set(), defaultValue) {
  return function (x) {
    return variants.includes(x) ? x : defaultValue
  }
}

export function asModel(ModelClass) {
  return null != ModelClass
    ? function (x) {
        return x instanceof ModelClass
          ? x
          : (null == x || ('object' == typeof x && 0 == Object.keys(x).length)) && 'function' == typeof ModelClass.empty
            ? ModelClass.empty()
            : ModelClass.of(x)
      }
    : function (/*x*/) {
        return null
      }
}

export function asImmutable(ImmutableClass) {
  return function (x) {
    return x instanceof ImmutableClass ? x : ImmutableClass(x)
  }
}

export function asImmutableMap(x) {
  return Map.isMap(x) ? x : null != x && 'object' == typeof x ? fromJS(x) : Map(x)
}

export function asImmutableList(x) {
  return List.isList(x) ? x : Array.isArray(x) ? fromJS(x) : List(x)
}

export function asInstance(check, build) {
  return function (x) {
    return check(x) ? x : build(x)
  }
}

export function asPlainObject(x) {
  return Iterable.isIterable(x) ? x.toJS() : x
}

export function asOptionalJSONFor(asSomething) {
  return function (x) {
    return 'string' == typeof x ? asSomething(JSON.parse(x)) : asSomething(x)
  }
}

export function withDefault(defaultValue, mapper, checkUnset = x => null == x) {
  return function (x) {
    return checkUnset(x) ? ('function' == typeof defaultValue ? defaultValue(x) : defaultValue) : mapper(x)
  }
}

export function asOr(check, mapper, otherMapper) {
  return function (x) {
    return check(x) ? mapper(x) : otherMapper(x)
  }
}
